import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { AppLayout } from '@/app/layouts/AppLayout';
import { PageLoader } from '@/components/loaders/Loader';
import { GuestRoute, ProtectedRoute, RoleRoute } from './guards';

const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const HomePage = lazy(() => import('@/features/home/HomePage'));
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const BatchesPage = lazy(() => import('@/features/batches/BatchesPage'));
const ReprocessBatchBuilderPage = lazy(() => import('@/features/batches/ReprocessBatchBuilderPage'));
const MyAssignmentsPage = lazy(() => import('@/features/assignments/MyAssignmentsPage'));
const VerificationPage = lazy(() => import('@/features/assignments/VerificationPage'));
const BeneficiaryVerificationPage = lazy(() => import('@/features/assignments/BeneficiaryVerificationPage'));
const BeneficiariesPage = lazy(() => import('@/features/beneficiaries/BeneficiariesPage'));
const BeneficiaryDetailPage = lazy(() => import('@/features/beneficiaries/BeneficiaryDetailPage'));
const CallingScreenPage = lazy(() => import('@/features/calls/CallingScreenPage'));
const AppointmentSchedulingPage = lazy(() => import('@/features/appointments/AppointmentSchedulingPage'));
const AppointmentsPage = lazy(() => import('@/features/appointments/AppointmentsPage'));
const CampsPage = lazy(() => import('@/features/appointments/CampsPage'));
const MyClusterBucketsPage = lazy(() => import('@/features/appointments/MyClusterBucketsPage'));
const ClusterBucketBeneficiariesPage = lazy(() => import('@/features/appointments/ClusterBucketBeneficiariesPage'));
const ClusterPlanningPage = lazy(() => import('@/features/cluster-planning/ClusterPlanningPage'));
const CampsPlanningPage = lazy(() => import('@/features/cluster-planning/CampsPlanningPage'));
const ManualCampPlanningPage = lazy(() => import('@/features/cluster-planning/ManualCampPlanningPage'));
const AgenticPlanningPage = lazy(() => import('@/features/agentic-planning/AgenticPlanningPage'));
const CampPlanningEnginePage = lazy(() => import('@/features/agentic-planning/CampPlanningEnginePage'));
const PincodeCampsPage = lazy(() => import('@/features/pincode-camps/PincodeCampsPage'));
const VisitPlanningPage = lazy(() => import('@/features/cluster-planning/VisitPlanningPage'));
const ClusterAssignmentsPage = lazy(() => import('@/features/cluster-planning/ClusterAssignmentsPage'));
const DistrictAssignmentsPage = lazy(() => import('@/features/cluster-planning/DistrictAssignmentsPage'));
const AllCampsPage = lazy(() => import('@/features/appointments/AllCampsPage'));
const MyCampsPage = lazy(() => import('@/features/appointments/MyCampsPage'));
const CampBeneficiariesPage = lazy(() => import('@/features/appointments/CampBeneficiariesPage'));
const AppointmentCallDispositionPage = lazy(() => import('@/features/appointments/AppointmentCallDispositionPage'));
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage'));
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'));
const ProfilePage = lazy(() => import('@/features/settings/ProfilePage'));
const CommunicationsPage = lazy(() => import('@/features/settings/CommunicationsPage'));
const SupportPage = lazy(() => import('@/features/settings/SupportPage'));
const PincodeValidationPage = lazy(() => import('@/features/support/PincodeValidationPage'));
const PincodeGeocodingPage = lazy(() => import('@/features/support/PincodeGeocodingPage'));
const AddressGeocodingPage = lazy(() => import('@/features/support/AddressGeocodingPage'));
const LocationGeocodingPage = lazy(() => import('@/features/support/LocationGeocodingPage'));
const BeneficiaryGenerationPage = lazy(() => import('@/features/support/BeneficiaryGenerationPage'));
const ManualCampMappingSupportPage = lazy(() => import('@/features/support/ManualCampMappingSupportPage'));
const ManageUsersPage = lazy(() => import('@/features/admin/ManageUsersPage'));
const AssignCampaignsPage = lazy(() => import('@/features/admin/AssignCampaignsPage'));
const AssignLabsPage = lazy(() => import('@/features/admin/AssignLabsPage'));
const UnauthorizedPage = lazy(() => import('@/features/auth/UnauthorizedPage'));
const NotFoundPage = lazy(() => import('@/features/auth/NotFoundPage'));
const ReportsHubPage = lazy(() => import('@/features/reports/ReportsHubPage'));
const TodaysAppointmentsReportPage = lazy(() => import('@/features/reports/TodaysAppointmentsReportPage'));
const CampwiseAppointmentStatusReportPage = lazy(() => import('@/features/reports/CampwiseAppointmentStatusReportPage'));
const CampwiseServiceStatusReportPage = lazy(() => import('@/features/reports/CampwiseServiceStatusReportPage'));
const SpecialistReferralsReportPage = lazy(() => import('@/features/reports/SpecialistReferralsReportPage'));
const FinalReportPage = lazy(() => import('@/features/reports/FinalReportPage'));
const ReferralReportsPage = lazy(() => import('@/features/reports/ReferralReportsPage'));
const LabSampleReportsPage = lazy(() => import('@/features/reports/LabSampleReportsPage'));
const LabBatchStatusReportPage = lazy(() => import('@/features/reports/LabBatchStatusReportPage'));
const AdvancedReportBuilderPage = lazy(() => import('@/features/reports/AdvancedReportBuilderPage'));

export function AppRouter(): React.JSX.Element {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route element={<GuestRoute />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>

        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/beneficiaries" element={<BeneficiariesPage />} />
            <Route path="/beneficiaries/:id" element={<BeneficiaryDetailPage />} />
            <Route path="/calls/:beneficiaryId" element={<CallingScreenPage />} />
            <Route path="/appointments" element={<AppointmentsPage />} />
            <Route path="/appointments/new/:beneficiaryId" element={<AppointmentSchedulingPage />} />
            <Route path="/communications" element={<CommunicationsPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/settings" element={<SettingsPage />} />

            <Route element={<RoleRoute roles={['admin', 'supervisor']} />}>
              <Route path="/batches" element={<BatchesPage />} />
              <Route path="/batches/:batchId" element={<VerificationPage />} />
              <Route path="/batches/:batchId/verify/:beneficiaryId" element={<BeneficiaryVerificationPage />} />
              <Route path="/cluster-planning" element={<ClusterPlanningPage />} />
              <Route path="/planning/cluster" element={<ClusterPlanningPage />} />
              <Route path="/planning/camps" element={<CampsPlanningPage />} />
              <Route path="/planning/manual-camps" element={<ManualCampPlanningPage />} />
              <Route path="/planning/manual-camp-mapping" element={<Navigate to="/support/manual-camp-mapping" replace />} />
              <Route path="/planning/agentic" element={<AgenticPlanningPage />} />
              <Route path="/planning/engine" element={<CampPlanningEnginePage />} />
              <Route path="/planning/pincode-camps" element={<PincodeCampsPage />} />
              <Route path="/planning/visit" element={<VisitPlanningPage />} />
              <Route path="/cluster-assignments" element={<ClusterAssignmentsPage />} />
              <Route path="/reports" element={<ReportsPage />} />
            </Route>

            <Route element={<RoleRoute roles={['admin']} />}>
              <Route path="/admin/users" element={<ManageUsersPage />} />
              <Route path="/admin/reprocess-batches" element={<ReprocessBatchBuilderPage />} />
              <Route path="/admin/reports" element={<ReportsHubPage />} />
              <Route path="/admin/reports/todays-appointments" element={<TodaysAppointmentsReportPage />} />
              <Route path="/admin/reports/campwise-appointment-status" element={<CampwiseAppointmentStatusReportPage />} />
              <Route path="/admin/reports/campwise-service-status" element={<CampwiseServiceStatusReportPage />} />
              <Route path="/admin/reports/specialist-referrals" element={<SpecialistReferralsReportPage />} />
              <Route path="/admin/reports/final-report" element={<FinalReportPage />} />
              <Route path="/admin/reports/referral-reports" element={<ReferralReportsPage />} />
              <Route path="/admin/reports/lab-sample-reports" element={<LabSampleReportsPage />} />
              <Route path="/admin/reports/lab-batch-status" element={<LabBatchStatusReportPage />} />
              <Route path="/admin/reports/advanced-report-builder" element={<AdvancedReportBuilderPage />} />
              <Route path="/admin/campaigns" element={<AssignCampaignsPage />} />
              <Route path="/admin/labs" element={<AssignLabsPage />} />
              <Route path="/district-assignments" element={<DistrictAssignmentsPage />} />
              <Route path="/camps" element={<CampsPage />} />
              <Route path="/support/pincode-validation" element={<PincodeValidationPage />} />
              <Route path="/support/pincode-geocoding" element={<PincodeGeocodingPage />} />
              <Route path="/support/address-geocoding" element={<AddressGeocodingPage />} />
              <Route path="/support/location-geocoding" element={<LocationGeocodingPage />} />
              <Route path="/support/beneficiary-generation" element={<BeneficiaryGenerationPage />} />
              <Route path="/support/manual-camp-mapping" element={<ManualCampMappingSupportPage />} />
              <Route path="/all-camps" element={<AllCampsPage />} />
              <Route path="/all-camps/:campId" element={<CampBeneficiariesPage />} />
            </Route>

            <Route element={<RoleRoute roles={['agent']} />}>
              <Route path="/assignments" element={<MyAssignmentsPage />} />
              <Route path="/assignments/:batchId" element={<VerificationPage />} />
              <Route path="/assignments/:batchId/verification" element={<VerificationPage />} />
              <Route path="/assignments/:batchId/verify/:beneficiaryId" element={<BeneficiaryVerificationPage />} />
              <Route path="/cluster-buckets" element={<MyClusterBucketsPage />} />
              <Route path="/cluster-buckets/:bucketId" element={<ClusterBucketBeneficiariesPage />} />
              <Route path="/cluster-buckets/:bucketId/disposition/:beneficiaryId" element={<AppointmentCallDispositionPage />} />
              <Route path="/my-camps" element={<MyCampsPage />} />
              <Route path="/my-camps/:campId" element={<CampBeneficiariesPage />} />
            </Route>

            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
